<template>
  <div class="hotel-facility-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('hotel_facilities.title') }}</h2>
      <b-button variant="primary" @click="createHotelFacility">
        <i class="fas fa-plus mr-2"></i>{{ $t('hotel_facilities.create_button') }}
      </b-button>
    </div>

    <CrudTable :columns="fields" :data-sources="hotelFacilities" :pagination="paginationInfo" :loader="isLoading"
      list-url="/hotel-facilities" @search="handleSearch" @filter="handleFilter" @refresh="fetchHotelFacilities"
      ref="action-table">
      <template #cell(hotel)="data">
        <div>{{ data.item.hotel?.name || '-' }}</div>
      </template>
      <template #cell(facility_name)="data">
        <div>
          <span v-for="facility in data.item.facilities" :key="facility.id"
            class="badge px-3 py-1 rounded-pill bg-secondary text-white" style="margin-right: 5px;">
            {{ facility.name }}
          </span>
        </div>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button variant="outline-primary" size="sm" @click="editHotelFacility(data.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>

    <ConfirmDeleteModal v-if="selectedHotelFacility" :show.sync="showDeleteModal" :item-id="selectedHotelFacility.id"
      delete-url="/hotel-facilities"
      :message="$t('hotel_facilities.confirm_delete_message', { hotel: selectedHotelFacility.hotel?.name })"
      success-message="$t('hotel_facilities.deleted_successfully')" error-message="$t('common.something_went_wrong')"
      @deleted="handleItemDeleted" />
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";

export default {
  name: "HotelFacilityList",
  components: {
    CrudTable,
    ConfirmDeleteModal
  },
  data() {
    return {
      hotelFacilities: [],
      isLoading: false,
      showDeleteModal: false,
      selectedHotelFacility: null,
      currentPage: 1,
      perPage: 10,
      sortBy: "created_at",
      sortDesc: true,
      filters: {
        search: "",
        status: null,
      },
      fields: [
        {
          key: "hotel",
          label: this.$t("hotel_facilities.table_header_hotel"),
          sortable: false,
        },
        {
          key: "facility_name",
          label: this.$t("hotel_facilities.table_header_facility_name"),
          sortable: false,
        },
        {
          key: "actions",
          label: this.$t("common.actions"),
        },
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0,
      },
    };
  },
  methods: {
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchHotelFacilities();
    },
    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchHotelFacilities();
    },
    createHotelFacility() {
      this.$router.push("/admin/hotel-facilities/create");
    },
    editHotelFacility(hotelFacility) {
      this.$router.push(`/admin/hotel-facilities/${hotelFacility.id}/edit`);
    },
    confirmDelete(hotelFacility) {
      this.selectedHotelFacility = hotelFacility;
      this.showDeleteModal = true;
    },
    handleItemDeleted() {
      this.$refs['action-table'].refreshTableData();
      this.$bvToast.toast(this.$t('hotel_facilities.deleted_successfully'), {
        title: this.$t('common.success'),
        variant: 'success',
        solid: true,
      });
      this.showDeleteModal = false;
      this.selectedHotelFacility = null;
    },
    async fetchHotelFacilities() {
      try {
        this.isLoading = true;
        const response = await request({
          method: "get",
          url: "/hotel-facilities",
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters,
          },
        });
        if (response.data && response.data.data) {
          this.hotelFacilities = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0,
            };
          }
        } else {
          this.hotelFacilities = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0,
          };
        }
      } catch (error) {
        console.error("Error fetching hotel facilities:", error);
        this.hotelFacilities = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0,
        };
      } finally {
        this.isLoading = false;
      }
    },
  },
  created() {
    this.fetchHotelFacilities();
  },
};
</script>

<style scoped>
.hotel-facility-list {
  padding: 20px;
}
</style>
